<template>
    <o-loading :active="isLoading" :fullPage="true">
        <o-icon
        class="icon icon__table-loader"
        pack="icon"
        icon="icon-ic-loader"
        style="font-size: 60px"
        />
    </o-loading>
    <div
        v-if="showExport"
        class="custom-class"
        @click.self="showExport = false"
    >
    <!-- :mainTag="placementTagDetails" -->
    <TagExport
      :selectedPlacement="selectedPlacement"
      :placements="placementId"
    />
    </div>
    <router-view> </router-view>
</template>
<script>
import { createApp, ref } from 'vue';
let Login, TagExport, Toast, Services;
import { AuthBackend } from "@/utils/authBackend";
const app = createApp({});

const bus = ref(null);

if (process.env.NODE_ENV === 'development') {
    app.config.devtools = true;
}

export default {
    name: "App",
    async mounted() {
        // Set dark mode as default
        document.documentElement.classList.add("dark");
        (window.location.pathname.includes("/preview/") || window.location.pathname.includes("/share/analytics/")) ? this.previewPage = true : this.previewPage = false;

        
        window.geoApiRendered = false;

        this.insertWhitelabelCss();
        this.fetchTenantInfo();
        await this.getGeoWeatherAPI();

        if (!this.previewPage) {
            // Dynamically import modules only if not preview page
            const [busModule, LoginModule, TagExportModule, ToastModule, ServicesModule] = await Promise.all([
                import("@/utils/bus"),
                import("@/components/login.vue"),
                import("./components/Dashboard/TagExport.vue"),
                import("@/components/reusable/Toast.vue"),
                import("@/services/Services"),
            ]);
            bus.value = busModule.default;
            Login = LoginModule.default;
            TagExport = TagExportModule.default;
            Toast = ToastModule.default;
            Services = ServicesModule.default;

            bus.value.on("sendToast", (data) => {
                this.openToast(null, data.message, data.type);
            });
            bus.value.on("user:login", this.login);
            bus.value.on("openTemplate", () => {
                this.isLoading = true;
            });

            bus.value.on("templateLoaded", () => {
                this.isLoading = false;
            });

            bus.value.on("showLoader", () => {
                this.isLoading = true;
            });

            bus.value.on("hideLoader", () => {
                this.isLoading = false;
            });

            bus.value.on("openExportTag", (tagdetails) => {
                this.showExport = true;
                this.selectedPlacement = tagdetails.selectedPlacement;
                this.placements = tagdetails.placements;
            });

            bus.value.on("closeTagPopUpNew", () => {
                this.showExport = false;
            });
        }
    },
    unmounted() {
        if (!this.previewPage && bus.value) {
            bus.value.off("user:login", this.login);
            bus.value.off("closeTagPopUpNew");
            bus.value.off("openTemplate", () => {
                this.isLoading = true;
            });
            bus.value.off("templateLoaded", () => {
                this.isLoading = false;
            });
            bus.value.off("openExportTag", (tagdetails) => {
                this.showExport = true;
                this.selectedPlacement = tagdetails.selectedPlacement;
                this.placements = tagdetails.placements;
            });
        }
    },
    components() {
        // Only register components if not previewPage and modules are loaded
        if (!this.previewPage && Login && TagExport) {
            return {
                Login,
                TagExport,
            };
        }
        return {};
    },
    data() {
        return {
            showLogin: false,
            isLoading: false,
            showExport: false,
            placementTagDetails: null,
            placementName: null,
            placementId: null,
            previewPage: false,
            tenant: null,
        };
    },
    provide() {
        return {
            bus,
        };
    },
    methods: {
        mergeContextObjects(baseValue, incomingValue) {
            const baseIsObject = baseValue && typeof baseValue === "object" && !Array.isArray(baseValue);
            const incomingIsObject = incomingValue && typeof incomingValue === "object" && !Array.isArray(incomingValue);

            if (!incomingIsObject) {
                return incomingValue;
            }

            const merged = baseIsObject ? { ...baseValue } : {};
            Object.keys(incomingValue).forEach((key) => {
                merged[key] = this.mergeContextObjects(merged[key], incomingValue[key]);
            });

            return merged;
        },

        getDeviceOs(userAgent) {
            if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
            if (/android/i.test(userAgent)) return "android";
            if (/windows/i.test(userAgent)) return "windows";
            if (/mac os x|macintosh/i.test(userAgent)) return "macos";
            if (/linux/i.test(userAgent)) return "linux";
            return "unknown";
        },

        getDeviceBrowser(userAgent) {
            if (/edg\//i.test(userAgent)) return "edge";
            if (/opr\//i.test(userAgent) || /opera/i.test(userAgent)) return "opera";
            if (/firefox/i.test(userAgent)) return "firefox";
            if (/chrome|crios/i.test(userAgent) && !/edg\//i.test(userAgent) && !/opr\//i.test(userAgent)) return "chrome";
            if (/safari/i.test(userAgent) && !/chrome|crios|edg\//i.test(userAgent)) return "safari";
            return "unknown";
        },

        getScreenSizeTier(width) {
            if (width < 480) return "small";
            if (width <= 768) return "medium";
            if (width <= 1440) return "large";
            return "xl";
        },

        getViewportSizeTier(width) {
            if (width < 480) return "xs";
            if (width <= 640) return "sm";
            if (width <= 768) return "md";
            if (width <= 1024) return "lg";
            return "xl";
        },

        buildDerivedContext() {
            const userAgent = (navigator.userAgent || "").toLowerCase();
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth || screen.width || 0;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || screen.height || 0;
            const screenWidth = window.screen?.width || viewportWidth;
            const orientation = viewportWidth > viewportHeight ? "landscape" : "portrait";

            const isTv = /smart-tv|smarttv|googletv|appletv|hbbtv|netcast|viera|tizen|web0s|webos|roku|aftb|aftm|bravia|crkey|tv/i.test(userAgent);
            const isTablet = !isTv && (/ipad|tablet/i.test(userAgent) || (/android/i.test(userAgent) && viewportWidth >= 768 && viewportWidth <= 1024));
            const isMobile = !isTv && !isTablet && (/mobi|iphone|ipod|android/i.test(userAgent) || viewportWidth < 768);
            const deviceType = isTv ? "tv" : isTablet ? "tablet" : isMobile ? "mobile" : "desktop";

            return {
                device: {
                    type: deviceType,
                    is_mobile: isMobile,
                    is_tablet: isTablet,
                    is_desktop: !isMobile && !isTablet && !isTv,
                    os: this.getDeviceOs(userAgent),
                    browser: this.getDeviceBrowser(userAgent),
                    orientation,
                    screen_size_tier: this.getScreenSizeTier(screenWidth),
                },
                viewport: {
                    size_tier: this.getViewportSizeTier(viewportWidth),
                    orientation,
                    width: viewportWidth,
                    height: viewportHeight,
                },
            };
        },

        ensureDerivedContext() {
            const existingContext = window.__ADCTVContext__ && typeof window.__ADCTVContext__ === "object"
                ? window.__ADCTVContext__
                : {};

            window.__ADCTVContext__ = this.mergeContextObjects(existingContext, this.buildDerivedContext());
        },

        async initNLS() {
            if (!this.previewPage && Services) {
                const language = Services.getUserService().getLanguage();
            }
        },
        openToast(heading, message, type) {
            if (!this.previewPage && Toast) {
                this.$oruga.notification.open({
                    component: Toast,
                    props: {
                        heading,
                        message,
                        type,
                    },
                    position: "top-right",
                    duration: 4000,
                });
            }
        },
        logout() {
            if (this.$route.name === "auth") {
                return;
            }
            this.showLogin = true;
        },
        login() {
            if (this.$route.name === "auth") {
                return;
            }
            this.showLogin = false;
        },

        insertWhitelabelCss() {
            const domain = window.location.hostname;
            const filename = domain.replace(/\.|-/g, "_");

            const head = document.head;

            const link_1 = document.createElement("link");
            link_1.type = "text/css";
            link_1.rel = "stylesheet";
            link_1.href = `/css/default.css`;
            head.appendChild(link_1);

            const link_2 = document.createElement("link");
            link_2.type = "text/css";
            link_2.rel = "stylesheet";
            link_2.href = `/storage/css/style_${filename}.css`;
            head.appendChild(link_2);
        },
        async fetchTenantInfo() {
            // clear previous tenant data
            this.tenant = null;
            localStorage.removeItem('tenantData');
            try {
                await AuthBackend.get(`/auth/get-tenant-data`)
                .then((response) => {
                    this.tenant = response.data.data;
                    if (this.tenant?.previewPageFavicon  && this.previewPage) {
                        this.insertTenantLogo(this.tenant.previewPageFavicon);
                    } else if (this.tenant?.favicon) {
                        this.insertTenantLogo(this.tenant.favicon);
                    }

                    // Save tenant data in localStorage
                    localStorage.setItem('tenantData', JSON.stringify(response.data.data));
                })
                .catch((error) => {
                    console.error('Failed to load tenant info:', error);
                });

            } catch (err) {
                console.error('Failed to load tenant info:', err);
            }
        },

        insertTenantLogo(logoUrl) {
            const link = document.querySelector('link[rel="icon"]') || document.createElement('link');
            link.rel = 'icon';
            link.href = logoUrl;
            document.head.appendChild(link);
        },

        

        getGeoWeatherAPI() {
            if(!window.geoApiRendered){
                window.geoApiRendered = true;
                this.ensureDerivedContext();
                return fetch("https://geow.adctv.com")
                    .then((response) => response.text())
                    .then((scriptContent) => {
                        const fetchedContext = scriptContent ? JSON.parse(scriptContent)?.context : {};
                        const existingContext = window.__ADCTVContext__ && typeof window.__ADCTVContext__ === "object"
                            ? window.__ADCTVContext__
                            : {};
                        window.__ADCTVContext__ = this.mergeContextObjects(
                            existingContext,
                            fetchedContext && typeof fetchedContext === "object" ? fetchedContext : {}
                        );
                        this.ensureDerivedContext();
                    })
                    .catch((error) => {
                        console.error("Error loading Geo Weather API:", error);
                        const existingContext = window.__ADCTVContext__ && typeof window.__ADCTVContext__ === "object"
                            ? window.__ADCTVContext__
                            : {};
                        window.__ADCTVContext__ = this.mergeContextObjects(existingContext, {
                            geo: { country: null, city: null, region: null, lat: null, lng: null, timezone: null },
                            network: { isp: null, asn: null, protocol: null, rtt: null, speed_tier: null, supports_rich_media: null, video_quality_tier: null },
                            bot: { score: null, is_bot: null, is_corporate_proxy: null },
                            time: { timestamp: null, hour: null, day_of_week: null, timezone: null, daypart: null, is_weekend: null, is_work_hours: null, is_commuting_hours: null, meal_period: null, productivity_state: null },
                            weather: { temp: null, feels_like: null, local_hour: null, humidity: null, condition: null, icon: "//wcdn.airtory.com/weather/64x64/day/116.png", text: null, is_day: null, precip: null, wind_speed: null, uv_index: null, sunrise: null, sunset: null, visibility: null, temperature_band: null, condition_type: null, uv_level: null, comfort_level: null, wind_level: null, is_daylight: null, precipitation_level: null, visibility_level: null, activity_suitability: null },
                            audience: { outdoor_score: null, outdoor_likelihood: null, commerce_intent: null, engagement_likelihood: null, mobility_state: null, session_mood: null, purchase_context: null },
                            traffic: { quality_tier: null, is_suspicious: null, trust_score: null },
                            device: { rich_media_ready: null, experience_tier: null, type: null, is_mobile: null, is_tablet: null, is_desktop: null, os: null, browser: null, orientation: null, screen_size_tier: null },
                            viewport: { size_tier: null, orientation: null },
                            media: { video_quality: null }
                        });
                        this.ensureDerivedContext();
                    });
            }

            this.ensureDerivedContext();
            return Promise.resolve(window.__ADCTVContext__);
        },
        
    },
};
</script>
<style lang="scss">
@use "@/scss/main.scss" as *;

.login-modal {
    background-color: #0f1318 !important;
    width: 34.5%;
    height: auto;
}
.o-load__overlay {
    background: rgba(25, 29, 36, 0.2) !important;
}
</style>

<style scoped>
.custom-class {
    overflow-y: auto;
    overflow-x: hidden;
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: 50;
    background-color: #000000d9; 
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 300ms ease-in-out;
}
</style>
<style>
@import url("./style/css/legacy.css");
</style>